// src/pages/AdminPage.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import "./Admin.css";
import { supabase } from "../lib/supabase";
import * as XLSX from "xlsx";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL || "";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (SUPABASE_URL ? `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1` : "http://localhost:4000/api");
const SUPABASE_ANON_KEY =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  "";

function AdminPage() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();

  const getAccessTokenOrLogout = async () => {
    if (token) return token;
    if (!supabase) return null;
    let { data } = await supabase.auth.getSession();
    let accessToken = data?.session?.access_token || null;
    // 로그인 직후 리다이렉트 시 세션 반영 지연 대응: 한 번 더 시도
    if (!accessToken) {
      await new Promise((r) => setTimeout(r, 400));
      const retry = await supabase.auth.getSession();
      accessToken = retry?.data?.session?.access_token || null;
    }
    if (!accessToken) {
      alert("세션이 만료되었습니다. 다시 로그인해 주세요.");
      logout();
      navigate("/login", { replace: true });
    }
    return accessToken;
  };

  // 탭 상태: 계정/업무분장표 vs 사용자별 수행업무
  const [activeTab, setActiveTab] = useState("accounts");

  // ==== 유저 관리 상태 ====
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    department: "",
    duty: "",
    role: "user",
  });
  const [creatingUser, setCreatingUser] = useState(false);

  const [editingUser, setEditingUser] = useState(null); // {id, name, role, tempPassword}

  // ==== 업무분장표 (job_tasks) 관리 상태 ====
  const [jobTasks, setJobTasks] = useState([]);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobError, setJobError] = useState("");

  const [newTask, setNewTask] = useState({
    category_id: "",
    category_name: "",
    activity_id: "",
    activity_desc: "",
    focus_code: "",
    focus_desc: "",
  });

  const [editingTask, setEditingTask] = useState(null); // {id, category_id, category_name, activity_id, activity_desc, focus_code, focus_desc}
  const editTaskFormRef = useRef(null);

  // 수정 폼이 나타나면 자동 스크롤
  useEffect(() => {
    if (editingTask && editTaskFormRef.current) {
      editTaskFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [editingTask]);

  // CSV 업로드 관련 상태
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ==== 사용자별 수행업무 (user_job_tasks) 관리 상태 ====
  const [userTasks, setUserTasks] = useState([]);
  const [userTasksLoading, setUserTasksLoading] = useState(true);
  const [userTasksError, setUserTasksError] = useState("");

  // 컬럼별 필터 상태
  const [filterUserId, setFilterUserId] = useState("");
  const [filterUserName, setFilterUserName] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterTaskName, setFilterTaskName] = useState("");
  const [filterFrequency, setFilterFrequency] = useState("");

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // ======= 유저 목록 조회 =======
  async function fetchUsers() {
    setLoading(true);
    setError("");
    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/f60b6398-d186-4779-bcbd-cbe3cecd1852',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H4',location:'AdminPage.jsx:fetchUsers',message:'fetchUsers called',data:{hasToken:!!token,tokenLength:token?.length || 0,apiBase:API_BASE_URL},timestamp:Date.now()})}).catch(()=>{});
      // #endregion

      const res = await fetch(`${API_BASE_URL}/admin-users`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `유저 목록 불러오기 실패 (${res.status})`);
      }

      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error("fetchUsers error:", err);
      setError(err.message || "유저 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }

  // ======= 업무분장표(job_tasks) 목록 조회 =======
  async function fetchJobTasks() {
    setJobLoading(true);
    setJobError("");
    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(`${API_BASE_URL}/admin-job-tasks`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
      });

      if (!res.ok) {
        throw new Error(`업무분장표 불러오기 실패 (${res.status})`);
      }

      const data = await res.json();
      const mapped = (data || []).map((t) => ({
        id: t.id,
        category_id: t.category_id || "",
        category_name: t.category_name || "",
        activity_id: t.activity_id || "",
        activity_desc: t.activity_desc || "",
        focus_code: t.focus_code || "",
        focus_desc: t.focus_desc || "",
        isActive: typeof t.is_active === "boolean" ? t.is_active : true,
      }));
      setJobTasks(mapped);
    } catch (err) {
      console.error("fetchJobTasks error:", err);
      setJobError(err.message || "업무분장표를 불러오지 못했습니다.");
    } finally {
      setJobLoading(false);
    }
  }

  // ======= 사용자별 수행업무(user_job_tasks) 목록 조회 =======
  async function fetchUserTasks() {
    setUserTasksLoading(true);
    setUserTasksError("");
    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(`${API_BASE_URL}/admin-user-tasks`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
      });

      if (!res.ok) {
        throw new Error(
          `사용자별 수행업무 불러오기 실패 (${res.status})`
        );
      }

      const data = await res.json();

      const mapped = (data || []).map((row) => ({
        id: row.id,
        userId: row.user_id,
        userName: row.user_name ?? row.userName ?? "",
        department: row.department ?? "",
        category: row.category || "",
        taskName: row.task_name ?? row.taskName ?? "",
        frequency: row.frequency || "",
        hours: row.hours,
        annualHours: row.annual_hours ?? row.annualHours ?? null,
        sortOrder: row.sort_order ?? row.sortOrder ?? null,
        taskStatus: row.task_status ?? row.status ?? "draft",
        hraRatio: row.hra_ratio ?? row.hraRatio ?? null,
      }));

      setUserTasks(mapped);
    } catch (err) {
      console.error("fetchUserTasks error:", err);
      setUserTasksError(
        err.message || "사용자별 수행업무를 불러오지 못했습니다."
      );
    } finally {
      setUserTasksLoading(false);
    }
  }

  async function handleResetUserTaskStatus(userId) {
    if (!token) {
      alert("인증 토큰이 없습니다. 다시 로그인해주세요.");
      return;
    }
    const ok = window.confirm("해당 사용자의 상태를 '작성 중'으로 변경할까요?");
    if (!ok) return;

    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(`${API_BASE_URL}/admin-user-tasks-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ userId }),
      });

      if (!res.ok) {
        throw new Error(`상태 변경 실패 (${res.status})`);
      }

      await fetchUserTasks();
    } catch (err) {
      console.error("reset user task status error:", err);
      alert(err.message || "상태 변경에 실패했습니다.");
    }
  }

  // 필터 적용된 userTasks
  const filteredUserTasks = useMemo(() => {
    return userTasks.filter((t) => {
      if (
        filterUserId &&
        !(t.department || "")
          .toLowerCase()
          .includes(filterUserId.toLowerCase())
      ) {
        return false;
      }
      if (
        filterUserName &&
        !(t.userName || "")
          .toLowerCase()
          .includes(filterUserName.toLowerCase())
      ) {
        return false;
      }
      if (
        filterCategory &&
        !(t.category || "").includes(filterCategory)
      ) {
        return false;
      }
      if (
        filterTaskName &&
        !(t.taskName || "").includes(filterTaskName)
      ) {
        return false;
      }
      if (
        filterFrequency &&
        (t.frequency || "") !== filterFrequency
      ) {
        return false;
      }
      return true;
    });
  }, [
    userTasks,
    filterUserId,
    filterUserName,
    filterCategory,
    filterTaskName,
    filterFrequency,
  ]);

  function handleExportUserTasksExcel() {
    if (filteredUserTasks.length === 0) {
      alert("내보낼 데이터가 없습니다.");
      return;
    }

    const excelRows = filteredUserTasks.map((t) => ({
      ID: t.id,
      "유저 이름": t.userName,
      소속: t.department,
      분류: t.category,
      수행업무: t.taskName,
      수행주기: t.frequency,
      "투입시간(시간)": t.hours ?? "",
      연간시간: t.annualHours ?? "",
      상태: t.taskStatus === "submitted" ? "최종제출" : "작성 중",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "사용자별수행업무");

    const now = new Date();
    const dateKey = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
    XLSX.writeFile(workbook, `user_job_tasks_export_${dateKey}.xlsx`);
  }

  useEffect(() => {
    // token이 있을 때만 데이터 로드
    if (token) {
      fetchUsers();
      fetchJobTasks();
      fetchUserTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ======= 새 유저 생성 =======
  async function handleCreateUser(e) {
    e.preventDefault();
    setError("");
    const payload = {
      name: String(newUser.name || "").trim(),
      email: String(newUser.email || "").trim(),
      password: String(newUser.password || ""),
      department: String(newUser.department || "").trim() || null,
      duty: String(newUser.duty || "").trim() || null,
      role: newUser.role || "user",
    };

    if (!payload.name || !payload.email || !payload.password) {
      setError("이름, 이메일, 초기 비밀번호를 모두 입력해주세요.");
      return;
    }
    if (payload.password.length < 6) {
      setError("초기 비밀번호는 최소 6자 이상이어야 합니다.");
      return;
    }
    setCreatingUser(true);

    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(`${API_BASE_URL}/admin-users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data.message ||
            (res.status === 409
              ? "이미 가입된 이메일입니다."
              : `유저 생성 실패 (${res.status})`)
        );
      }

      setNewUser({
        name: "",
        email: "",
        password: "",
        department: "",
        duty: "",
        role: "user",
      });
      await fetchUsers();
      alert("유저가 생성되었습니다.");
    } catch (err) {
      console.error("handleCreateUser error:", err);
      const message =
        err?.message === "Failed to fetch"
          ? "유저 생성 요청에 실패했습니다. 네트워크 또는 API 연결 상태를 확인해주세요."
          : err?.message || "유저 생성 중 오류가 발생했습니다.";
      setError(message);
    } finally {
      setCreatingUser(false);
    }
  }

  // ======= 유저 수정 시작 =======
  function startEdit(u) {
    setEditingUser({
      id: u.id,
      name: u.name,
      role: u.role,
      tempPassword: "",
    });
  }

  // ======= 유저 수정 저장 =======
  async function handleUpdateUser(e) {
    e.preventDefault();
    if (!editingUser) return;
    setError("");

    if (!token) {
      setError("인증 토큰이 없습니다. 다시 로그인해주세요.");
      return;
    }

    if (
      editingUser.tempPassword &&
      String(editingUser.tempPassword).trim().length < 6
    ) {
      setError("임시 비밀번호는 최소 6자 이상이어야 합니다.");
      return;
    }

    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(
        `${API_BASE_URL}/admin-users/${editingUser.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            name: editingUser.name,
            role: editingUser.role,
            password: editingUser.tempPassword
              ? String(editingUser.tempPassword).trim()
              : undefined,
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "유저 수정 실패");
      }

      setEditingUser(null);
      await fetchUsers();
      if (editingUser.tempPassword) {
        alert("임시 비밀번호로 변경되었습니다. 사용자는 다음 로그인 시 비밀번호를 다시 변경해야 합니다.");
      }
    } catch (err) {
      console.error("handleUpdateUser error:", err);
      setError(err.message || "유저 수정 중 오류가 발생했습니다.");
    }
  }

  // ======= 유저 삭제 =======
  async function handleDeleteUser(id) {
    if (!window.confirm("정말 이 유저를 삭제하시겠습니까?")) return;

    if (!token) {
      setError("인증 토큰이 없습니다. 다시 로그인해주세요.");
      return;
    }

    setError("");
    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(`${API_BASE_URL}/admin-users/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "유저 삭제 실패");
      }

      await fetchUsers();
    } catch (err) {
      console.error("handleDeleteUser error:", err);
      setError(err.message || "유저 삭제 중 오류가 발생했습니다.");
    }
  }

  // ======= 새 업무(task) 생성 =======
  async function handleCreateTask(e) {
    e.preventDefault();
    setJobError("");

    if (!token) {
      setJobError("인증 토큰이 없습니다. 다시 로그인해주세요.");
      return;
    }

    if (!newTask.category_name || !newTask.activity_desc) {
      setJobError("분류명과 활동 설명을 모두 입력하세요.");
      return;
    }

    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(`${API_BASE_URL}/admin-job-tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          category_id: newTask.category_id || null,
          category_name: newTask.category_name,
          activity_id: newTask.activity_id || null,
          activity_desc: newTask.activity_desc,
          focus_code: newTask.focus_code || null,
          focus_desc: newTask.focus_desc || null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "업무 생성 실패");
      }

      setNewTask({
        category_id: "",
        category_name: "",
        activity_id: "",
        activity_desc: "",
        focus_code: "",
        focus_desc: "",
      });
      await fetchJobTasks();
    } catch (err) {
      console.error("handleCreateTask error:", err);
      setJobError(err.message || "업무 생성 중 오류가 발생했습니다.");
    }
  }

  // ======= 업무 수정 시작 =======
  function startEditTask(t) {
    setEditingTask({
      id: t.id,
      category_id: t.category_id || "",
      category_name: t.category_name || "",
      activity_id: t.activity_id || "",
      activity_desc: t.activity_desc || "",
      focus_code: t.focus_code || "",
      focus_desc: t.focus_desc || "",
    });
  }

  // ======= 업무 수정 저장 =======
  async function handleUpdateTask(e) {
    e.preventDefault();
    if (!editingTask) return;
    setJobError("");

    if (!token) {
      setJobError("인증 토큰이 없습니다. 다시 로그인해주세요.");
      return;
    }

    if (!editingTask.category_name || !editingTask.activity_desc) {
      setJobError("분류명과 활동 설명을 모두 입력하세요.");
      return;
    }

    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(
        `${API_BASE_URL}/admin-job-tasks/${editingTask.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            category_id: editingTask.category_id || null,
            category_name: editingTask.category_name,
            activity_id: editingTask.activity_id || null,
            activity_desc: editingTask.activity_desc,
            focus_code: editingTask.focus_code || null,
            focus_desc: editingTask.focus_desc || null,
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "업무 수정 실패");
      }

      setEditingTask(null);
      await fetchJobTasks();
    } catch (err) {
      console.error("handleUpdateTask error:", err);
      setJobError(err.message || "업무 수정 중 오류가 발생했습니다.");
    }
  }

  // ======= CSV 템플릿 다운로드 =======
  async function handleDownloadTemplate() {
    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(`${API_BASE_URL}/admin-job-tasks-template`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
      });

      if (!res.ok) {
        throw new Error("템플릿 다운로드 실패");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "job_tasks_template.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("handleDownloadTemplate error:", err);
      setJobError(err.message || "템플릿 다운로드 중 오류가 발생했습니다.");
    }
  }

  // ======= CSV 파일 업로드 =======
  async function handleCsvUpload(e) {
    e.preventDefault();
    
    if (!csvFile) {
      setJobError("CSV 파일을 선택해주세요.");
      return;
    }

    if (!token) {
      setJobError("인증 토큰이 없습니다. 다시 로그인해주세요.");
      return;
    }

    setUploading(true);
    setJobError("");

    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const formData = new FormData();
      formData.append("file", csvFile);

      const res = await fetch(`${API_BASE_URL}/admin-job-tasks-bulk-upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `업로드 실패 (${res.status})`);
      }

      const data = await res.json();
      
      // 성공 메시지 표시
      alert(`성공적으로 업로드되었습니다.\n- 추가: ${data.created || 0}건\n- 업데이트: ${data.updated || 0}건\n- 실패: ${data.failed || 0}건`);
      
      // 파일 선택 초기화
      setCsvFile(null);
      const fileInput = document.getElementById("csv-file-input");
      if (fileInput) {
        fileInput.value = "";
      }

      // 목록 새로고침
      await fetchJobTasks();
    } catch (err) {
      console.error("handleCsvUpload error:", err);
      setJobError(err.message || "CSV 업로드 중 오류가 발생했습니다.");
    } finally {
      setUploading(false);
    }
  }

  // ======= 업무 삭제 =======
  async function handleDeleteTask(id) {
    if (!window.confirm("정말 이 업무를 삭제하시겠습니까?")) return;

    if (!token) {
      setJobError("인증 토큰이 없습니다. 다시 로그인해주세요.");
      return;
    }

    setJobError("");
    try {
      const accessToken = await getAccessTokenOrLogout();
      if (!accessToken) return;
      const res = await fetch(`${API_BASE_URL}/admin-job-tasks/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "업무 삭제 실패");
      }

      await fetchJobTasks();
    } catch (err) {
      console.error("handleDeleteTask error:", err);
      setJobError(err.message || "업무 삭제 중 오류가 발생했습니다.");
    }
  }

  return (
    <div className="admin-page">
      <div className="admin-card">
        {/* 헤더 */}
        <header className="admin-header">
          <div className="admin-title-wrap">
            <h1 className="admin-title">관리자 페이지</h1>
            <p className="admin-subtitle">
              {user?.name} 님 (role: {user?.role}) · 유저 계정을 조회 / 생성 /
              수정 / 삭제하고, 업무 관련 데이터를 관리할 수 있습니다.
            </p>
          </div>
          <div className="admin-header-right">
            <span className="admin-badge">ADMIN</span>
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={handleLogout}
              style={{ marginLeft: "8px" }}
            >
              로그아웃
            </button>
          </div>
        </header>

        {/* 상단 에러 메시지 (계정/업무 관리용) */}
        {error && <div className="admin-message-error">{error}</div>}

        {/* 탭 버튼 */}
        <div className="admin-tabs">
          <button
            type="button"
            className={
              activeTab === "accounts"
                ? "admin-tab admin-tab-active"
                : "admin-tab"
            }
            onClick={() => setActiveTab("accounts")}
          >
            계정 / 업무분장표
          </button>
          <button
            type="button"
            className={
              activeTab === "userTasks"
                ? "admin-tab admin-tab-active"
                : "admin-tab"
            }
            onClick={() => setActiveTab("userTasks")}
          >
            사용자별 수행업무 (user_job_tasks)
          </button>
        </div>

        <div className="admin-body">
          {/* ===== Tab 1: 계정 / 업무분장표 관리 ===== */}
          {activeTab === "accounts" && (
            <>
              {/* 왼쪽: 유저 목록 */}
              <section className="admin-section">
                <div className="admin-section-header">
                  <div>
                    <h2 className="admin-section-title">유저 목록</h2>
                    <p className="admin-section-desc">
                      등록된 계정을 조회하고 권한을 수정하거나 삭제할 수
                      있습니다.
                    </p>
                  </div>
                </div>

                {loading ? (
                  <div className="admin-message-info">불러오는 중...</div>
                ) : (
                  <div className="admin-table-wrapper">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>이름</th>
                          <th>이메일</th>
                          <th>권한</th>
                          <th>액션</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length === 0 ? (
                          <tr>
                            <td colSpan={5}>등록된 유저가 없습니다.</td>
                          </tr>
                        ) : (
                          users.map((u) => (
                            <tr key={u.id}>
                              <td>{u.id}</td>
                              <td>{u.name}</td>
                              <td>{u.email}</td>
                              <td>{u.role}</td>
                              <td>
                                <div className="admin-actions">
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-secondary"
                                    onClick={() => startEdit(u)}
                                  >
                                    수정
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-danger"
                                    onClick={() => handleDeleteUser(u.id)}
                                  >
                                    삭제
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* 오른쪽: 새 유저 등록 + 수정 */}
              <section className="admin-section">
                <div className="admin-section-header">
                  <div>
                    <h2 className="admin-section-title">새 유저 등록</h2>
                    <p className="admin-section-desc">
                      관리자 또는 일반 사용자 계정을 직접 생성할 수 있습니다.
                    </p>
                  </div>
                </div>

                {/* 새 유저 등록 폼 */}
                <form className="admin-form" onSubmit={handleCreateUser}>
                  <div className="admin-form-row">
                    <label className="admin-label">이름</label>
                    <input
                      className="admin-input"
                      type="text"
                      value={newUser.name}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      required
                      placeholder="이름을 입력하세요"
                    />
                  </div>

                  <div className="admin-form-row">
                    <label className="admin-label">이메일</label>
                    <input
                      className="admin-input"
                      type="email"
                      value={newUser.email}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      required
                      placeholder="name@company.com"
                    />
                  </div>

                  <div className="admin-form-row">
                    <label className="admin-label">초기 비밀번호</label>
                    <input
                      className="admin-input"
                      type="password"
                      value={newUser.password}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      required
                      placeholder="초기 비밀번호"
                    />
                  </div>

                  <div className="admin-form-row">
                    <label className="admin-label">소속부서</label>
                    <input
                      className="admin-input"
                      type="text"
                      value={newUser.department}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          department: e.target.value,
                        }))
                      }
                      placeholder="예: 인사팀 / 개발팀 / 영업본부"
                    />
                  </div>

                  <div className="admin-form-row">
                    <label className="admin-label">담당업무</label>
                    <input
                      className="admin-input"
                      type="text"
                      value={newUser.duty}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          duty: e.target.value,
                        }))
                      }
                      placeholder="예: 채용 담당 / 백엔드 개발 / 데이터 분석"
                    />
                  </div>

                  <div className="admin-form-row">
                    <label className="admin-label">권한</label>
                    <select
                      className="admin-select"
                      value={newUser.role}
                      onChange={(e) =>
                        setNewUser((prev) => ({
                          ...prev,
                          role: e.target.value,
                        }))
                      }
                    >
                      <option value="user">일반 사용자</option>
                      <option value="admin">관리자</option>
                    </select>
                  </div>

                  <div className="admin-actions">
                    <button
                      type="submit"
                      className="admin-btn admin-btn-primary"
                      disabled={creatingUser}
                    >
                      {creatingUser ? "생성 중..." : "유저 생성"}
                    </button>
                  </div>
                </form>

                {/* 유저 수정 폼 */}
                {editingUser && (
                  <>
                    <div
                      className="admin-section-header"
                      style={{ marginTop: 16 }}
                    >
                      <div>
                        <h2 className="admin-section-title">
                          유저 수정 (ID: {editingUser.id})
                        </h2>
                        <p className="admin-section-desc">
                          이름/권한을 변경하거나 임시 비밀번호를 설정한 뒤 저장을 눌러주세요.
                        </p>
                      </div>
                    </div>

                    <form
                      className="admin-form"
                      onSubmit={handleUpdateUser}
                    >
                      <div className="admin-form-row">
                        <label className="admin-label">이름</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={editingUser.name}
                          onChange={(e) =>
                            setEditingUser((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <div className="admin-form-row">
                        <label className="admin-label">권한</label>
                        <select
                          className="admin-select"
                          value={editingUser.role}
                          onChange={(e) =>
                            setEditingUser((prev) => ({
                              ...prev,
                              role: e.target.value,
                            }))
                          }
                        >
                          <option value="user">일반 사용자</option>
                          <option value="admin">관리자</option>
                        </select>
                      </div>

                      <div className="admin-form-row">
                        <label className="admin-label">임시 비밀번호 (선택)</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={editingUser.tempPassword || ""}
                          onChange={(e) =>
                            setEditingUser((prev) => ({
                              ...prev,
                              tempPassword: e.target.value,
                            }))
                          }
                          placeholder="입력 시 즉시 비밀번호가 변경됩니다 (최소 6자)"
                        />
                      </div>

                      <div className="admin-actions">
                        <button
                          type="submit"
                          className="admin-btn admin-btn-primary"
                        >
                          저장
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-secondary"
                          onClick={() => setEditingUser(null)}
                        >
                          취소
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </section>

              {/* 업무분장표 관리 섹션 */}
              <section className="admin-section admin-section-full" style={{ marginTop: 24 }}>
                <div className="admin-section-header">
                  <div>
                    <h2 className="admin-section-title">업무분장표 관리</h2>
                    <p className="admin-section-desc">
                      분류와 수행업무 항목을 추가 · 수정 · 삭제할 수 있습니다.
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary"
                      onClick={handleDownloadTemplate}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      CSV 템플릿 다운로드
                    </button>
                  </div>
                </div>

                {/* 에러/로딩 메시지 */}
                {jobError && (
                  <div
                    className="admin-message-error"
                    style={{ marginBottom: 8 }}
                  >
                    {jobError}
                  </div>
                )}

                {jobLoading ? (
                  <div className="admin-message-info">
                    업무분장표를 불러오는 중...
                  </div>
                ) : (
                  <div className="admin-table-wrapper">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>분류ID</th>
                          <th>분류명</th>
                          <th>활동ID</th>
                          <th>활동설명</th>
                          <th>포커스코드</th>
                          <th>포커스설명</th>
                          <th>상태</th>
                          <th>액션</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobTasks.length === 0 ? (
                          <tr>
                            <td colSpan={8}>
                              등록된 업무 항목이 없습니다.
                            </td>
                          </tr>
                        ) : (
                          jobTasks.map((t) => (
                            <tr key={t.id}>
                              <td>{t.category_id || ""}</td>
                              <td>{t.category_name || ""}</td>
                              <td>{t.activity_id || ""}</td>
                              <td>{t.activity_desc || ""}</td>
                              <td>{t.focus_code || ""}</td>
                              <td>{t.focus_desc || ""}</td>
                              <td>{t.isActive ? "사용" : "미사용"}</td>
                              <td>
                                <div className="admin-actions">
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-secondary"
                                    onClick={() => startEditTask(t)}
                                  >
                                    수정
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-danger"
                                    onClick={() =>
                                      handleDeleteTask(t.id)
                                    }
                                  >
                                    삭제
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* 업무 수정 폼 - 테이블 바로 아래에 표시 */}
                {editingTask && (
                  <div ref={editTaskFormRef} style={{ marginTop: 16, padding: "16px", backgroundColor: "#fffbeb", borderRadius: "8px", border: "1px solid #fcd34d" }}>
                    <h3
                      className="admin-section-title"
                      style={{ fontSize: 16, color: "#92400e" }}
                    >
                      업무 항목 수정 (ID: {editingTask.id})
                    </h3>
                    <form
                      className="admin-form"
                      onSubmit={handleUpdateTask}
                    >
                      <div className="admin-form-row">
                        <label className="admin-label">category_id</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={editingTask.category_id}
                          onChange={(e) =>
                            setEditingTask((prev) => ({
                              ...prev,
                              category_id: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <div className="admin-form-row">
                        <label className="admin-label">category_name *</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={editingTask.category_name}
                          onChange={(e) =>
                            setEditingTask((prev) => ({
                              ...prev,
                              category_name: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>

                      <div className="admin-form-row">
                        <label className="admin-label">activity_id</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={editingTask.activity_id}
                          onChange={(e) =>
                            setEditingTask((prev) => ({
                              ...prev,
                              activity_id: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <div className="admin-form-row">
                        <label className="admin-label">activity_desc *</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={editingTask.activity_desc}
                          onChange={(e) =>
                            setEditingTask((prev) => ({
                              ...prev,
                              activity_desc: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>

                      <div className="admin-form-row">
                        <label className="admin-label">focus_code</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={editingTask.focus_code}
                          onChange={(e) =>
                            setEditingTask((prev) => ({
                              ...prev,
                              focus_code: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <div className="admin-form-row">
                        <label className="admin-label">focus_desc</label>
                        <input
                          className="admin-input"
                          type="text"
                          value={editingTask.focus_desc}
                          onChange={(e) =>
                            setEditingTask((prev) => ({
                              ...prev,
                              focus_desc: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <div className="admin-actions">
                        <button
                          type="submit"
                          className="admin-btn admin-btn-primary"
                        >
                          저장
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn-secondary"
                          onClick={() => setEditingTask(null)}
                        >
                          취소
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* CSV 대량 업로드 */}
                <div style={{ marginTop: 16, marginBottom: 24, padding: "16px", backgroundColor: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                  <h3
                    className="admin-section-title"
                    style={{ fontSize: 16, marginBottom: 12 }}
                  >
                    CSV 대량 업로드
                  </h3>
                  <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                    CSV 파일을 업로드하여 여러 업무 항목을 한 번에 추가하거나 업데이트할 수 있습니다.
                  </p>
                  <form onSubmit={handleCsvUpload} style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <input
                        id="csv-file-input"
                        type="file"
                        accept=".csv"
                        onChange={(e) => setCsvFile(e.target.files[0])}
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                    <button
                      type="submit"
                      className="admin-btn admin-btn-primary"
                      disabled={uploading || !csvFile}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {uploading ? "업로드 중..." : "업로드"}
                    </button>
                  </form>
                </div>

                {/* 새 업무 등록 폼 */}
                <div style={{ marginTop: 16 }}>
                  <h3
                    className="admin-section-title"
                    style={{ fontSize: 16 }}
                  >
                    새 업무 항목 추가
                  </h3>
                  <form className="admin-form" onSubmit={handleCreateTask}>
                    <div className="admin-form-row">
                      <label className="admin-label">category_id</label>
                      <input
                        className="admin-input"
                        type="text"
                        value={newTask.category_id}
                        onChange={(e) =>
                          setNewTask((prev) => ({
                            ...prev,
                            category_id: e.target.value,
                          }))
                        }
                        placeholder="예: 1"
                      />
                    </div>

                    <div className="admin-form-row">
                      <label className="admin-label">category_name *</label>
                      <input
                        className="admin-input"
                        type="text"
                        value={newTask.category_name}
                        onChange={(e) =>
                          setNewTask((prev) => ({
                            ...prev,
                            category_name: e.target.value,
                          }))
                        }
                        required
                        placeholder="예: 보상 (Compensation)"
                      />
                    </div>

                    <div className="admin-form-row">
                      <label className="admin-label">activity_id</label>
                      <input
                        className="admin-input"
                        type="text"
                        value={newTask.activity_id}
                        onChange={(e) =>
                          setNewTask((prev) => ({
                            ...prev,
                            activity_id: e.target.value,
                          }))
                        }
                        placeholder="예: 1"
                      />
                    </div>

                    <div className="admin-form-row">
                      <label className="admin-label">activity_desc *</label>
                      <input
                        className="admin-input"
                        type="text"
                        value={newTask.activity_desc}
                        onChange={(e) =>
                          setNewTask((prev) => ({
                            ...prev,
                            activity_desc: e.target.value,
                          }))
                        }
                        required
                        placeholder="예: 보상제도에 대한 비즈니스 니즈를 파악"
                      />
                    </div>

                    <div className="admin-form-row">
                      <label className="admin-label">focus_code</label>
                      <input
                        className="admin-input"
                        type="text"
                        value={newTask.focus_code}
                        onChange={(e) =>
                          setNewTask((prev) => ({
                            ...prev,
                            focus_code: e.target.value,
                          }))
                        }
                        placeholder="예: 100"
                      />
                    </div>

                    <div className="admin-form-row">
                      <label className="admin-label">focus_desc</label>
                      <input
                        className="admin-input"
                        type="text"
                        value={newTask.focus_desc}
                        onChange={(e) =>
                          setNewTask((prev) => ({
                            ...prev,
                            focus_desc: e.target.value,
                          }))
                        }
                        placeholder="예: 전략 (Strategy)"
                      />
                    </div>

                    <div className="admin-actions">
                      <button
                        type="submit"
                        className="admin-btn admin-btn-primary"
                      >
                        업무 항목 추가
                      </button>
                    </div>
                  </form>
                </div>

              </section>
            </>
          )}

          {/* ===== Tab 2: 사용자별 수행업무(user_job_tasks) 관리 ===== */}
          {activeTab === "userTasks" && (
            <section className="admin-section admin-section-full">
              <div className="admin-section-header">
                <div>
                  <h2 className="admin-section-title">
                    사용자별 수행업무 (user_job_tasks)
                  </h2>
                  <p className="admin-section-desc">
                    메인 페이지에서 저장된 각 사용자별 수행업무를 조회할 수
                    있습니다. 상단 필터를 사용하여 유저/업무 기준으로 빠르게
                    찾을 수 있습니다.
                  </p>
                </div>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={handleExportUserTasksExcel}
                  disabled={userTasksLoading || filteredUserTasks.length === 0}
                >
                  엑셀 내보내기
                </button>
              </div>

              {userTasksError && (
                <div
                  className="admin-message-error"
                  style={{ marginBottom: 8 }}
                >
                  {userTasksError}
                </div>
              )}

              {userTasksLoading ? (
                <div className="admin-message-info">
                  사용자별 수행업무를 불러오는 중...
                </div>
              ) : (
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>유저 이름</th>
                        <th>소속</th>
                        <th>분류</th>
                        <th>수행업무</th>
                        <th>수행주기</th>
                        <th>투입시간(시간)</th>
                        <th>연간시간</th>
                        <th>상태</th>
                        <th>조치</th>
                      </tr>
                      {/* 필터 행 */}
                      <tr className="admin-filter-row">
                        <th></th>
                        <th>
                          <input
                            className="admin-input admin-input-small"
                            placeholder="이름 검색"
                            value={filterUserName}
                            onChange={(e) =>
                              setFilterUserName(e.target.value)
                            }
                          />
                        </th>
                        <th>
                          <input
                            className="admin-input admin-input-small"
                            placeholder="소속 검색"
                            value={filterUserId}
                            onChange={(e) =>
                              setFilterUserId(e.target.value)
                            }
                          />
                        </th>
                        <th>
                          <input
                            className="admin-input admin-input-small"
                            placeholder="분류 검색"
                            value={filterCategory}
                            onChange={(e) =>
                              setFilterCategory(e.target.value)
                            }
                          />
                        </th>
                        <th>
                          <input
                            className="admin-input admin-input-small"
                            placeholder="업무 검색"
                            value={filterTaskName}
                            onChange={(e) =>
                              setFilterTaskName(e.target.value)
                            }
                          />
                        </th>
                        <th>
                          <select
                            className="admin-select admin-input-small"
                            value={filterFrequency}
                            onChange={(e) =>
                              setFilterFrequency(e.target.value)
                            }
                          >
                            <option value="">전체</option>
                            <option value="연간">연간</option>
                            <option value="반기">반기</option>
                            <option value="분기">분기</option>
                            <option value="월간">월간</option>
                            <option value="주간">주간</option>
                            <option value="일간">일간</option>
                          </select>
                        </th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUserTasks.length === 0 ? (
                        <tr>
                          <td colSpan={10}>
                            조건에 맞는 수행업무가 없습니다.
                          </td>
                        </tr>
                      ) : (
                        filteredUserTasks.map((t) => (
                          <tr key={t.id}>
                            <td>{t.id}</td>
                            <td>{t.userName}</td>
                            <td>{t.department}</td>
                            <td>{t.category}</td>
                            <td>{t.taskName}</td>
                            <td>{t.frequency}</td>
                            <td>{t.hours ?? ""}</td>
                            <td>{t.annualHours ?? ""}</td>
                            <td>
                              {t.taskStatus === "submitted"
                                ? "최종제출"
                                : "작성 중"}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="admin-btn admin-btn-secondary"
                                onClick={() => handleResetUserTaskStatus(t.userId)}
                                disabled={t.taskStatus !== "submitted"}
                              >
                                작성 중으로 변경
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPage;